// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { ExtractProps } from '@Molecules/Details/Details';
import { SecurityTagType } from '@Types';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import {
    PAPValues,
    SeverityValues,
    TLPValues,
    AlertOutput,
    CustomStatusMapType,
    AlertStatusStage,
    UserOutput,
    CaseOutput,
} from 'thehive-sdk';
import { AlertEventList } from './eventList';
import { THSecurityTag } from '@Molecules/Inputs/THSecurityTag/THSecurityTag';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import THTag from '@Atoms/THTag/THTag';
import { FormatDate } from '@Molecules/Format';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { normalizeDate } from '@Helpers/date';
import { MessageDescriptor } from 'react-intl';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { convertToLocaleId } from '@Helpers/locale';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { AssigneeForm } from '@Molecules/AssigneeForm/AssigneeForm';

const { OptGroup } = THSelect;

interface ExtractDataProps {
    locale: (label: string | MessageDescriptor) => string;
    data: AlertOutput;
    eventList: AlertEventList;
    hasPermissions?: boolean;
}

export const extractData = ({
    locale,
    data: { _id: alertId },
    hasPermissions,
    eventList,
}: ExtractDataProps): Record<any, ExtractProps> => ({
    '#computeSecurity': {
        render: (values: { tlp: TLPValues; pap: PAPValues; severity: SeverityValues }, data) => (
            <div className="case-details__security">
                <div className="severity">
                    <THEditable<Partial<CaseOutput>>
                        key={'severity'}
                        name={'severity'}
                        initialValues={{ ['severity']: values['severity'] }}
                        onFinish={(values) => eventList.onEdit(values, alertId, data.stage === AlertStatusStage.NEW)}
                        submitOnChange
                    >
                        <THSecurityTag
                            type={'severity' as SecurityTagType}
                            disabled={!hasPermissions}
                            hasFullSeverityLabel
                        />
                    </THEditable>
                </div>
                <div className="pap_tlp">
                    {Object.entries(values)
                        .filter(([key]) => key !== 'severity')
                        .map(([key, value]) => (
                            <THEditable<Partial<CaseOutput>>
                                key={key}
                                name={key}
                                initialValues={{ [key]: value }}
                                onFinish={(values) =>
                                    eventList.onEdit(values, alertId, data.stage === AlertStatusStage.NEW)
                                }
                                submitOnChange
                            >
                                <THSecurityTag
                                    type={key as SecurityTagType}
                                    disabled={!hasPermissions}
                                    hasFullSeverityLabel
                                />
                            </THEditable>
                        ))}
                </div>
            </div>
        ),
    },
    '#computeAssignee': {
        className: 'case-details__assignee',
        render: ({ assignableUsers, assignee }: { assignableUsers: UserOutput[]; assignee: string }): ReactElement => (
            <AssigneeForm<AlertOutput>
                assignableUsers={assignableUsers}
                currentAssignee={assignee}
                onFinish={(values) => {
                    if (values) {
                        eventList.onEdit(values, alertId);
                    }
                }}
                disabled={!hasPermissions}
            />
        ),
    },
    source: {
        name: locale('alert.source'),
        render: (source) => <CopyValue value={source}>{source}</CopyValue>,
    },
    sourceRef: {
        name: locale('alert.reference'),
        render: (sourceRef, { externalLink }) =>
            externalLink && externalLink.trim() !== '' ? (
                <CopyValue value={externalLink}>
                    <ExternalLink url={externalLink}>{sourceRef}</ExternalLink>
                </CopyValue>
            ) : (
                <span>{sourceRef}</span>
            ),
    },
    type: {
        name: locale('alert.type'),
        render: (type) => <CopyValue value={type}>{type}</CopyValue>,
    },
    date: {
        render: (date: number, data) => (
            <THEditable<Partial<AlertOutput>>
                name="date"
                label={locale('common.occurred-date')}
                initialValues={{ date }}
                onFinish={(values) => eventList.onEdit(values, alertId, data.stage === AlertStatusStage.NEW)}
                formItemProps={{ normalize: normalizeDate }}
            >
                <THDatePicker
                    borderless
                    disabled={!hasPermissions}
                    placeholder="Enter a start date..."
                    allowClear={false}
                />
            </THEditable>
        ),
    },
    // '#computeStatus': {
    //     name: locale('common.status'),
    //     className: 'case-detail__status',
    //     isForce: false,
    //     render: ({
    //         statusList,
    //         status,
    //         isImported = false,
    //     }: {
    //         status: string;
    //         statusList: CustomStatusMapType;
    //         isImported: boolean;
    //     }) => {
    //         const colour = Object.values(statusList)
    //             .flat()
    //             .find((s) => s.value === status)?.colour;
    //         return (
    //             <AlertChangeStatus
    //                 triggerElem={
    //                     <CustomColorLabel
    //                         disabled={!hasPermissions || isImported}
    //                         value={locale({
    //                             id: `alert.data.status.${convertToLocaleId(status)}`,
    //                             defaultMessage: status,
    //                         })}
    //                         color={colour}
    //                     />
    //                 }
    //                 onFinish={(alertData) => eventList.onEdit(alertData, alertId)}
    //                 alertStatusList={statusList}
    //                 initialValues={{ status, summary: '' }}
    //             />
    //         );
    //     },
    // },
    '#computeStatus': {
        className: 'case-detail__status',
        isForce: false,
        render: (
            {
                statusList,
                status,
                isImported = false,
            }: {
                status: string;
                statusList: CustomStatusMapType;
                isImported: boolean;
            },
            data,
        ) => (
            <THEditable<Partial<AlertOutput>>
                name="status"
                label={locale('common.status')}
                initialValues={{ status }}
                onFinish={(values) => eventList.onEdit(values, alertId, data.stage === AlertStatusStage.NEW)}
            >
                <THSelect borderless disabled={!hasPermissions || isImported}>
                    {Object.entries(statusList).map(([key, values]) => (
                        <OptGroup key={key} label={key}>
                            {values.map(({ value, colour }) => (
                                <THSelect.Option key={value} value={value}>
                                    <CustomColorLabel
                                        value={locale({
                                            id: `alert.data.status.${convertToLocaleId(value)}`,
                                            defaultMessage: value,
                                        })}
                                        color={colour}
                                    />
                                </THSelect.Option>
                            ))}
                        </OptGroup>
                    ))}
                </THSelect>
            </THEditable>
        ),
    },
    '#computeCase': {
        name: locale('common.case'),
        render: ({ onClick, caseNumber }: any) => <THTag full value={`#${caseNumber}`} textBlack onClick={onClick} />,
    },
    importDate: {
        name: locale('common.import-date'),
        render: (importDate: number) => <FormatDate date={importDate} />,
    },
    timeToDetect: {
        name: locale('common.timeToDetect'),
        isForce: false,
        render: (timeToDetect: number) => <FormatDate date={timeToDetect} onlyFrom endDate={0} withoutSuffix />,
    },
    timeToAcknowledge: {
        name: locale('common.timeToAcknowledge'),
        isForce: false,
        render: (timeToAcknowledge: number) => (
            <FormatDate date={timeToAcknowledge} onlyFrom endDate={0} withoutSuffix />
        ),
    },
    timeToResolve: {
        name: locale('common.timeToResolve'),
        isForce: false,
        render: (timeToResolve: number) => <FormatDate date={timeToResolve} onlyFrom endDate={0} withoutSuffix />,
    },
    timeToTriage: {
        name: locale('common.timeToTriage'),
        isForce: false,
        render: (timeToTriage: number) => <FormatDate date={timeToTriage} onlyFrom endDate={0} withoutSuffix />,
    },
    timeToQualify: {
        name: locale('common.timeToQualify'),
        isForce: false,
        render: (timeToQualify: number) => <FormatDate date={timeToQualify} onlyFrom endDate={0} withoutSuffix />,
    },
});
